// src/data/expenses.ts — expenses, their splits, and settlements.

import { supabase } from '../lib/supabase';
import { run, runExpectingRows, unwrap } from '../lib/errors';
import type { ResolvedSplit, SplitMode } from '../lib/split';
import type { ExpenseRow, SettlementRow } from './types';

/**
 * Expenses with their splits embedded. PostgREST resolves the nested select
 * through the foreign key, so this is one round trip rather than one per
 * expense — which matters on hotel wifi behind a VPN.
 *
 * Soft-deleted expenses are filtered here rather than by a policy, matching
 * how trip_member_balances excludes them (addendum section 5).
 */
export async function listExpenses(tripId: string): Promise<ExpenseRow[]> {
  return unwrap<ExpenseRow[]>(
    supabase
      .from('trip_expenses')
      .select('*, trip_expense_splits(*)')
      .eq('trip_id', tripId)
      .is('deleted_at', null)
      .order('spent_at', { ascending: false }),
  );
}

export interface ExpenseInput {
  tripId: string;
  payerMemberId: string;
  description: string;
  category: string;
  currency: string;
  /** base-currency units per one unit of `currency`; frozen onto the row. */
  rate: number;
  mode: SplitMode;
  spentAt: string;
  /** Straight from resolveSplit(). Never assembled by hand at a call site. */
  split: ResolvedSplit;
}

function rpcArgs(input: ExpenseInput) {
  return {
    p_payer_member_id: input.payerMemberId,
    p_description: input.description,
    p_category: input.category,
    p_amount: input.split.amount,
    p_currency: input.currency,
    p_rate: input.rate,
    p_base_amount: input.split.baseAmount,
    p_split_mode: input.mode,
    p_spent_at: input.spentAt,
    p_splits: input.split.shares.map((s) => ({
      member_id: s.memberId,
      amount: s.amount,
      base_amount: s.baseAmount,
    })),
  };
}

/**
 * One RPC, not two inserts — non-negotiable #6. trip_expense_splits_sum_check
 * is DEFERRABLE INITIALLY DEFERRED, so the expense and its splits have to
 * commit together or the first write fails on its own.
 */
export async function createExpense(input: ExpenseInput): Promise<string> {
  return unwrap<string>(
    supabase.rpc('create_expense_with_splits', { p_trip_id: input.tripId, ...rpcArgs(input) }),
  );
}

/**
 * Full replace of the expense and its splits, in one transaction.
 *
 * The rate travels with the edit rather than being looked up again: editing a
 * typo in a description must not silently re-convert the amount at today's
 * rate. Non-negotiable #2 — refreshing FX never rewrites history.
 */
export async function updateExpense(expenseId: string, input: ExpenseInput): Promise<string> {
  return unwrap<string>(
    supabase.rpc('update_expense_with_splits', { p_expense_id: expenseId, ...rpcArgs(input) }),
  );
}

export async function deleteExpense(expenseId: string): Promise<void> {
  await runExpectingRows(
    supabase
      .from('trip_expenses')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', expenseId)
      .select('id'),
    'That expense could not be deleted — it may already be gone.',
  );
}

export async function listSettlements(tripId: string): Promise<SettlementRow[]> {
  return unwrap<SettlementRow[]>(
    supabase
      .from('trip_settlements')
      .select('*')
      .eq('trip_id', tripId)
      .is('deleted_at', null)
      .order('settled_at', { ascending: false }),
  );
}

export async function recordSettlement(input: {
  tripId: string;
  fromMemberId: string;
  toMemberId: string;
  baseAmount: number;
}): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  await run(
    supabase
      .from('trip_settlements')
      .insert({
        trip_id: input.tripId,
        from_member_id: input.fromMemberId,
        to_member_id: input.toMemberId,
        base_amount: input.baseAmount,
        created_by: userData.user?.id ?? null,
      })
      .select('id'),
  );
}

/** Soft delete, for undo — the contract's DELETE /settlements/{id}. */
export async function undoSettlement(settlementId: string): Promise<void> {
  await runExpectingRows(
    supabase
      .from('trip_settlements')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', settlementId)
      .select('id'),
    'That payment could not be undone — it may already be gone.',
  );
}
